import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule, FormBuilder, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputNumberModule } from 'primeng/inputnumber';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { AddInstituteService } from '../../../../services/super-admin/add-institute.service';
import { Rankingrequest } from '../../../../models/Institutedetails/Rankingrequest';
import { scrollToFirstInvalidControl } from '../../../../direct-apply/shared/validators/form-validation.utils';

@Component({
    selector: 'app-rankings',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        InputTextModule,
        ButtonModule,
        DropdownModule,
        InputTextareaModule,
        InputNumberModule,
        AccordionModule,
        CalendarModule,
        CheckboxModule
    ],
    templateUrl: './rankings.component.html',
    styleUrls: ['./rankings.component.scss']
})
export class RankingsComponent implements OnInit, OnChanges {
    @Input() form!: FormGroup;
    @Input() instituteId: string | null = null;
    @Input() isEditMode: boolean = false;
    @Output() onSaveSuccess = new EventEmitter<any>();
    @Output() onSaveError = new EventEmitter<any>();

    /** Editable rows for the SubjectRanks dictionary (subject name -> rank). */
    subjectRanksRows: FormArray = new FormArray<FormGroup>([]);
    /** Guards against feedback loops while syncing rows <-> the dictionary control. */
    private syncingFromRows = false;

    constructor(
        private fb: FormBuilder,
        private addInstituteService: AddInstituteService
    ) { }

    ngOnChanges(changes: SimpleChanges) { }

    ngOnInit() {
        if (this.form) {
            const mandatoryRankingFields = ['qsRanking', 'countryRanking'];
            const nonMandatoryRankingFields = ['usNewsRanking', 'indiaTodayRanking', 'nirfRanking'];

            mandatoryRankingFields.forEach(field => {
                if (!this.form.contains(field)) {
                    this.form.addControl(
                        field,
                        this.fb.control(null)
                    );
                } else {
                    const control = this.form.get(field);
                    control?.clearValidators();
                    control?.updateValueAndValidity();
                }
            });

            nonMandatoryRankingFields.forEach(field => {
                if (!this.form.contains(field)) {
                    this.form.addControl(
                        field,
                        this.fb.control(null)
                    );
                } else {
                    const control = this.form.get(field);
                    control?.clearValidators();
                    control?.updateValueAndValidity();
                }
            });

            // SubjectRanks is a dictionary (subject -> rank). The control holds the dictionary
            // object while the user edits it through a list of subject/rank rows.
            if (!this.form.contains('subjectRanks')) {
                this.form.addControl('subjectRanks', this.fb.control(null));
            } else {
                const control = this.form.get('subjectRanks');
                control?.clearValidators();
                control?.updateValueAndValidity();
            }

            // Build rows from the current value, then keep them in sync when the parent patches
            // the control later (edit mode loads the institute asynchronously).
            this.buildSubjectRanksRows(this.form.get('subjectRanks')?.value);
            this.form.get('subjectRanks')?.valueChanges.subscribe(value => {
                if (!this.syncingFromRows) {
                    this.buildSubjectRanksRows(value);
                }
            });

            // ✅ Only add vibes controls if they don't already exist
            // (they are pre-populated from parent in edit mode)
            const campusCultureFields = ['chill', 'competitive', 'startup', 'research', 'creative', 'traditional'];
            campusCultureFields.forEach(field => {
                if (!this.form.contains(field)) {
                    this.form.addControl(field, this.fb.control(false));
                }
                // If already exists, leave it — parent already patched the correct value
            });
        }
    }

    save() {
        if (this.form.valid) {
            if (!this.instituteId) {
                this.onSaveError.emit({ message: 'Institute ID is missing. Please save Basic Information first.' });
                return;
            }
            const formValue = this.form.value;

            const payload: Rankingrequest = {
                id: String(this.instituteId),
                qsRanking: formValue.qsRanking || '',
                countryRanking: formValue.countryRanking || 0,
                usNewsRanking: formValue.usNewsRanking || 0,
                indiaTodayRanking: formValue.indiaTodayRanking || '',
                nirfRanking: formValue.nirfRanking || '',
                subjectRanks: formValue.subjectRanks || {},
                vibes: {
                    chill: formValue.chill ?? false,
                    competitive: formValue.competitive ?? false,
                    startup: formValue.startup ?? false,
                    research: formValue.research ?? false,
                    creative: formValue.creative ?? false,
                    traditional: formValue.traditional ?? false
                }
            };

            this.addInstituteService.updateRankingDetails(payload).subscribe({
                next: (res: any) => {
                    this.onSaveSuccess.emit({ mode: 'edit', response: res });
                },
                error: (err: any) => {
                    this.onSaveError.emit(err);
                }
            });
        } else {
            this.markFormGroupTouched(this.form);
            this.subjectRanksRows.markAllAsTouched();
            scrollToFirstInvalidControl();
            const invalidFields = this.getInvalidFields();
            this.onSaveError.emit({ validationError: true, fields: invalidFields });
        }
    }

    // ---- SubjectRanks (dictionary) helpers -------------------------------------------------

    /** At least one valid subject/rank entry is required. */
    private subjectRanksRequired = (control: AbstractControl): ValidationErrors | null => {
        return null;
    };

    private newSubjectRankRow(subject: string = '', rank: number | null = null): FormGroup {
        const group = this.fb.group({
            subject: [subject],
            rank: [rank]
        });
        group.valueChanges.subscribe(() => this.writeRowsToControl());
        return group;
    }

    /** Rebuilds the editable rows from a dictionary value (used on init / parent patch). */
    private buildSubjectRanksRows(value: { [subject: string]: number } | null | undefined): void {
        this.subjectRanksRows.clear({ emitEvent: false });
        if (value && typeof value === 'object') {
            Object.keys(value).forEach(subject =>
                this.subjectRanksRows.push(this.newSubjectRankRow(subject, value[subject]), { emitEvent: false })
            );
        }
        if (this.subjectRanksRows.length === 0) {
            this.subjectRanksRows.push(this.newSubjectRankRow(), { emitEvent: false });
        }
    }

    /** Collapses the rows back into the dictionary stored on the form control. */
    private writeRowsToControl(): void {
        const dict: { [subject: string]: number } = {};
        this.subjectRanksRows.controls.forEach(row => {
            const subject = (row.get('subject')?.value ?? '').toString().trim();
            const rankValue = row.get('rank')?.value;
            const rank = Number(rankValue);
            if (subject && rankValue !== null && rankValue !== '' && Number.isInteger(rank)) {
                dict[subject] = rank;
            }
        });
        this.syncingFromRows = true;
        this.form.get('subjectRanks')?.setValue(Object.keys(dict).length ? dict : null, { emitEvent: false });
        this.form.get('subjectRanks')?.updateValueAndValidity({ emitEvent: false });
        this.syncingFromRows = false;
    }

    addSubjectRank(): void {
        this.subjectRanksRows.push(this.newSubjectRankRow());
    }

    removeSubjectRank(index: number): void {
        this.subjectRanksRows.removeAt(index);
        this.writeRowsToControl();
    }

    private getInvalidFields(): string[] {
        const invalid: string[] = [];
        const controls = this.form.controls;
        for (const name in controls) {
            if (controls[name].invalid) {
                const label = name
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase());
                invalid.push(label);
            }
        }
        return invalid;
    }

    private markFormGroupTouched(formGroup: FormGroup | FormArray) {
        Object.values(formGroup.controls).forEach(control => {
            if (control instanceof FormGroup || control instanceof FormArray) {
                this.markFormGroupTouched(control);
            } else {
                control.markAsTouched();
                control.markAsDirty();
            }
        });
    }
}